import React from 'react';
import { useNavigate } from 'react-router-dom';
import { dpsTheme } from '../../../styles/dpsTheme';
import { ArrowRight, TrendingUp } from 'lucide-react';

interface Action {
  id: string;
  title: string;
  description: string;
  priority: 'high' | 'medium' | 'low';
  cost: number;
  points: number;
}

interface PriorityActionsProps {
  actions: Action[];
  reportId: string;
  topAction?: {
    title: string;
    context: string;
    budget: string;
    gain: string;
  };
}

export const PriorityActions: React.FC<PriorityActionsProps> = ({ actions, reportId, topAction }) => {
  const navigate = useNavigate();

  const priorityConfig = {
    high: {
      label: 'Haute',
      color: '#ef4444',
      bg: 'rgba(239, 68, 68, 0.1)',
    },
    medium: {
      label: 'Moyenne',
      color: '#f59e0b',
      bg: 'rgba(245, 158, 11, 0.1)',
    },
    low: {
      label: 'Basse',
      color: '#3b82f6',
      bg: 'rgba(59, 130, 246, 0.1)',
    },
  };

  // CORRECTION 1 : Tri intelligent des actions
  // 1. Toutes les Hautes d'abord
  // 2. Puis Moyennes pour compléter
  // 3. Basses uniquement si < 5 actions Hautes+Moyennes
  const sortedActions = [...actions].sort((a, b) => {
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return priorityOrder[a.priority] - priorityOrder[b.priority];
  });

  const topActions = sortedActions.slice(0, 5);

  return (
    <div
      style={{
        background: dpsTheme.colors.background.card,
        backdropFilter: 'blur(20px)',
        border: `1px solid ${dpsTheme.colors.border.primary}`,
        borderRadius: dpsTheme.borderRadius.xl,
        padding: '24px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <h3
        style={{
          fontSize: dpsTheme.typography.fontSize.xl,
          fontWeight: dpsTheme.typography.fontWeight.bold,
          color: dpsTheme.colors.text.primary,
          marginBottom: '20px',
        }}
      >
        Actions prioritaires
      </h3>

      {topAction && (
        <div
          style={{
            background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05))',
            border: '2px solid #ef4444',
            borderRadius: dpsTheme.borderRadius.lg,
            padding: '16px',
            marginBottom: '20px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '8px',
            }}
          >
            <div
              style={{
                background: '#ef4444',
                color: 'white',
                fontSize: dpsTheme.typography.fontSize.xs,
                fontWeight: dpsTheme.typography.fontWeight.bold,
                padding: '4px 10px',
                borderRadius: dpsTheme.borderRadius.full,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Priorité absolue
            </div>
          </div>
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize.base,
              fontWeight: dpsTheme.typography.fontWeight.bold,
              color: dpsTheme.colors.text.primary,
              marginBottom: '6px',
            }}
          >
            {topAction.title}
          </div>
          <div
            style={{
              fontSize: dpsTheme.typography.fontSize.sm,
              color: dpsTheme.colors.text.secondary,
              marginBottom: '12px',
              lineHeight: '1.5',
            }}
          >
            {topAction.context}
          </div>
          <div
            style={{
              display: 'flex',
              gap: '12px',
              fontSize: dpsTheme.typography.fontSize.sm,
            }}
          >
            <div>
              <span style={{ color: dpsTheme.colors.text.muted }}>Budget : </span>
              <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.accent.blue.DEFAULT }}>
                {topAction.budget}
              </span>
            </div>
            <div>
              <span style={{ color: dpsTheme.colors.text.muted }}>Gain : </span>
              <span style={{ fontWeight: dpsTheme.typography.fontWeight.semibold, color: dpsTheme.colors.accent.green.DEFAULT }}>
                {topAction.gain}
              </span>
            </div>
          </div>
        </div>
      )}

      <div style={{ flex: 1, marginBottom: '16px' }}>
        {topActions.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: dpsTheme.colors.text.muted,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <TrendingUp size={48} style={{ opacity: 0.3 }} />
            </div>
            <p style={{ fontSize: dpsTheme.typography.fontSize.base, marginBottom: '8px' }}>
              Aucune action en attente
            </p>
            <p style={{ fontSize: dpsTheme.typography.fontSize.sm }}>
              Toutes vos actions ont été complétées !
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {topActions.map((action) => {
              const config = priorityConfig[action.priority];
              return (
                <div
                  key={action.id}
                  style={{
                    background: dpsTheme.colors.background.cardHover,
                    border: `1px solid ${dpsTheme.colors.border.primary}`,
                    borderRadius: dpsTheme.borderRadius.lg,
                    padding: '14px',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = dpsTheme.colors.accent.blue.border;
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = dpsTheme.colors.border.primary;
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                  onClick={() => navigate(`/dps/report/${reportId}/actions`)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    <div
                      style={{
                        background: config.bg,
                        color: config.color,
                        fontSize: dpsTheme.typography.fontSize.xs,
                        fontWeight: dpsTheme.typography.fontWeight.semibold,
                        padding: '4px 8px',
                        borderRadius: dpsTheme.borderRadius.md,
                        flexShrink: 0,
                        marginTop: '2px',
                      }}
                    >
                      {config.label}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: dpsTheme.typography.fontSize.base,
                          fontWeight: dpsTheme.typography.fontWeight.semibold,
                          color: dpsTheme.colors.text.primary,
                          marginBottom: '6px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {action.title}
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: '12px',
                          fontSize: dpsTheme.typography.fontSize.sm,
                          color: dpsTheme.colors.text.muted,
                        }}
                      >
                        <span>{action.cost.toLocaleString('fr-FR')}€</span>
                        <span>•</span>
                        <span style={{ color: dpsTheme.colors.accent.green.DEFAULT }}>
                          +{action.points} pts
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <button
        onClick={() => navigate(`/dps/report/${reportId}/actions`)}
        style={{
          width: '100%',
          background: dpsTheme.colors.accent.blue.DEFAULT,
          color: 'white',
          border: 'none',
          borderRadius: dpsTheme.borderRadius.lg,
          padding: '14px',
          fontSize: dpsTheme.typography.fontSize.base,
          fontWeight: dpsTheme.typography.fontWeight.semibold,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          transition: 'all 0.2s ease',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = dpsTheme.colors.accent.blue.hover;
          e.currentTarget.style.transform = 'translateY(-1px)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = dpsTheme.colors.accent.blue.DEFAULT;
          e.currentTarget.style.transform = 'translateY(0)';
        }}
      >
        Voir toutes les actions
        <ArrowRight size={18} />
      </button>
    </div>
  );
};